// frontend/src/pages/Login.js
import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { login, clearError } from '../../store/slices/authSlice';
import { useNavigate, Link } from 'react-router-dom';
import mayfairLogo from '../../logo.svg';

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [sessionExpired, setSessionExpired] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [emailFocused, setEmailFocused] = useState(false);
    const [passwordFocused, setPasswordFocused] = useState(false);
    const [shake, setShake] = useState(false);
    const [loginAttempts, setLoginAttempts] = useState(0);
    const [isLocked, setIsLocked] = useState(false);
    const [lockTimer, setLockTimer] = useState(0);
    const [localError, setLocalError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const formRef = useRef(null);
    const shakeTimeoutRef = useRef(null);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loading, error, user } = useSelector((state) => state.auth);

    // Check if redirected due to session expiry
    useEffect(() => {
        if (localStorage.getItem('sessionExpired') === 'true') {
            setSessionExpired(true);
            localStorage.removeItem('sessionExpired');
            const timer = setTimeout(() => setSessionExpired(false), 8000);
            return () => clearTimeout(timer);
        }
    }, []);

    // Sync Redux error with local error
    useEffect(() => {
        if (error) {
            console.log('[Login] Redux error received:', error);
            setLocalError(error);
            setShake(true);
            if (shakeTimeoutRef.current) {
                clearTimeout(shakeTimeoutRef.current);
            }
            shakeTimeoutRef.current = setTimeout(() => setShake(false), 500);
            setIsSubmitting(false);
        }
    }, [error]);

    // Auto-fill email from saved cookie
    useEffect(() => {
        const savedEmail = document.cookie
            .split('; ')
            .find(row => row.startsWith('saved_email='));
        if (savedEmail) {
            setEmail(decodeURIComponent(savedEmail.split('=')[1]));
            setRememberMe(true);
        }
    }, []);

    // Lockout timer
    useEffect(() => {
        if (isLocked && lockTimer > 0) {
            const interval = setInterval(() => {
                setLockTimer(prev => prev - 1);
            }, 1000);
            return () => clearInterval(interval);
        } else if (isLocked && lockTimer === 0) {
            setIsLocked(false);
            setLoginAttempts(0);
            setLocalError('');
        }
    }, [isLocked, lockTimer]);

    // Redirect if already logged in
    useEffect(() => {
        if (user) {
            console.log('[Login] User already logged in, redirecting to dashboard');
            navigate('/dashboard');
        }
    }, [user, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log('[Login] Form submitted');
        setLocalError('');
        setShake(false);
        setIsSubmitting(true);

        // Check if locked
        if (isLocked) {
            setLocalError(`Account is locked. Please wait ${lockTimer} seconds.`);
            setShake(true);
            if (shakeTimeoutRef.current) {
                clearTimeout(shakeTimeoutRef.current);
            }
            shakeTimeoutRef.current = setTimeout(() => setShake(false), 500);
            setIsSubmitting(false);
            return;
        }

        // Validate inputs
        if (!email || !password) {
            console.log('[Login] Missing email or password');
            setLocalError('Please enter both email and password.');
            setShake(true);
            if (shakeTimeoutRef.current) {
                clearTimeout(shakeTimeoutRef.current);
            }
            shakeTimeoutRef.current = setTimeout(() => setShake(false), 500);
            setIsSubmitting(false);
            return;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setLocalError('Please enter a valid email address.');
            setShake(true);
            if (shakeTimeoutRef.current) {
                clearTimeout(shakeTimeoutRef.current);
            }
            shakeTimeoutRef.current = setTimeout(() => setShake(false), 500);
            setIsSubmitting(false);
            return;
        }

        if (rememberMe) {
            document.cookie = `saved_email=${encodeURIComponent(email)}; max-age=${30 * 24 * 60 * 60}; path=/`;
        } else {
            document.cookie = 'saved_email=; max-age=0; path=/';
        }

        try {
            console.log('[Login] Attempting login for:', email);
            const result = await dispatch(login({ email, password })).unwrap();
            if (result.requiresPasswordChange) {
                navigate('/first-login-password', { state: { temporaryPassword: password } });
                return;
            }
            console.log('[Login] Login successful:', result);
            setLoginAttempts(0);
            setLocalError('');
            setIsSubmitting(false);
        } catch (err) {
            console.log('[Login] Login failed with error:', err);
            console.log('[Login] Error type:', typeof err);

            const newAttempts = loginAttempts + 1;
            setLoginAttempts(newAttempts);

            let errorMessage = 'Invalid email or password. Please try again.';
            if (typeof err === 'string') {
                errorMessage = err;
            } else if (err && typeof err === 'object') {
                if (err.message) {
                    errorMessage = err.message;
                } else if (err.error) {
                    errorMessage = err.error;
                } else if (err.payload) {
                    errorMessage = err.payload;
                }
            }

            console.log('[Login] Setting error message:', errorMessage);
            setLocalError(errorMessage);
            setShake(true);
            if (shakeTimeoutRef.current) {
                clearTimeout(shakeTimeoutRef.current);
            }
            shakeTimeoutRef.current = setTimeout(() => setShake(false), 500);
            setIsSubmitting(false);

            if (newAttempts >= 5) {
                setIsLocked(true);
                setLockTimer(60);
                setLocalError(`Too many failed attempts. Please wait 60 seconds before trying again.`);
            }
        }
    };

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        if (localError) {
            setLocalError('');
            dispatch(clearError());
        }
        setShake(false);
    };

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
        if (localError) {
            setLocalError('');
            dispatch(clearError());
        }
        setShake(false);
    };

    const clearErrorMessage = () => {
        setLocalError('');
        dispatch(clearError());
    };

    const getErrorMessage = () => {
        if (!localError && !error) return '';
        const msg = localError || error || '';

        if (msg.toLowerCase().includes('password') ||
            msg.toLowerCase().includes('credentials') ||
            msg.toLowerCase().includes('invalid')) {
            return '❌ Invalid email or password. Please try again.';
        }
        if (msg.toLowerCase().includes('deactivated') || msg.toLowerCase().includes('inactive')) {
            return '🚫 Your account has been deactivated. Please contact your administrator.';
        }
        if (msg.toLowerCase().includes('network') || msg.toLowerCase().includes('connection')) {
            return '🌐 Network error. Please check your connection.';
        }
        if (msg.toLowerCase().includes('locked')) {
            return `🔒 ${msg}`;
        }
        return `❌ ${msg}`;
    };

    const isLoading = loading || isSubmitting;

    // Handle forgot password navigation
    const handleForgotPassword = (e) => {
        e.preventDefault();
        navigate('/forgot-password');
    };

    return (
        <div className="min-h-screen flex bg-white overflow-hidden animate-fadeIn">
            {/* ========== LEFT BRAND PANEL ========== */}
            <div className="hidden lg:flex lg:w-5/12 xl:w-1/2 relative bg-gradient-to-br from-[#1a3327] via-[#2d5232] to-[#3a663f] text-white items-center justify-center p-12 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                        <defs>
                            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" opacity="0.3" />
                            </pattern>
                            <radialGradient id="glow1" cx="30%" cy="30%">
                                <stop offset="0%" stopColor="#4d8c54" stopOpacity="0.3" />
                                <stop offset="100%" stopColor="#4d8c54" stopOpacity="0" />
                            </radialGradient>
                            <radialGradient id="glow2" cx="70%" cy="70%">
                                <stop offset="0%" stopColor="#2d5232" stopOpacity="0.3" />
                                <stop offset="100%" stopColor="#2d5232" stopOpacity="0" />
                            </radialGradient>
                        </defs>
                        <rect width="100" height="100" fill="url(#grid)" />
                        <rect width="100" height="100" fill="url(#glow1)" />
                        <rect width="100" height="100" fill="url(#glow2)" />
                    </svg>
                </div>

                <div className="absolute top-1/4 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-float"></div>
                <div className="absolute bottom-1/4 left-0 w-80 h-80 bg-white/5 rounded-full blur-3xl animate-float-delayed"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>

                <div className="relative max-w-md text-center space-y-10 z-10">
                    <div className="relative inline-flex items-center justify-center w-32 h-32 rounded-3xl bg-white/10 backdrop-blur-xl border border-white/20 shadow-2xl p-4 mx-auto">
                        <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/20 to-transparent animate-pulse-slow"></div>
                        <img
                            src={mayfairLogo}
                            alt="Mayfair Insurance Marine Portal"
                            className="w-full h-full object-contain relative z-10"
                        />
                    </div>

                    <div className="space-y-4">
                        <h1 className="text-5xl font-extrabold tracking-tight leading-tight bg-gradient-to-r from-white to-green-200 bg-clip-text text-transparent">
                            Mayfair Insurance Marine Portal
                        </h1>
                        <p className="text-lg text-green-200/90 leading-relaxed max-w-sm mx-auto">
                            Instant marine cargo quotes, and full
                            certificate management – all in one place.
                        </p>
                    </div>

                    <div className="flex flex-wrap justify-center gap-6 text-sm text-green-100/80">
                        <div className="flex items-center gap-2.5 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/10">
                            <svg className="w-5 h-5 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            IRA Compliant
                        </div>
                        <div className="flex items-center gap-2.5 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/10">
                            <svg className="w-5 h-5 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            Secure
                        </div>
                        <div className="flex items-center gap-2.5 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/10">
                            <svg className="w-5 h-5 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            Fast
                        </div>
                    </div>


                </div>
            </div>

            {/* ========== RIGHT FORM PANEL ========== */}
            <div className="flex-1 flex items-center justify-center px-6 py-12 bg-gradient-to-b from-gray-50 to-white lg:bg-white">
                <div className="w-full max-w-md space-y-8">
                    {/* Mobile logo */}
                    <div className="lg:hidden text-center space-y-3">
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-[#2d5232] to-[#3a663f] shadow-xl shadow-green-200 p-2">
                            <img
                                src={mayfairLogo}
                                alt="Mayfair Insurance"
                                className="w-full h-full object-contain"
                            />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900">Welcome Back</h2>
                        <p className="text-sm text-gray-500">Sign in to your account</p>
                    </div>

                    {/* Form Card */}
                    <div ref={formRef} className={`bg-white rounded-3xl shadow-2xl border border-gray-100/80 p-8 md:p-10 space-y-8 transition-all duration-500 ${shake ? 'animate-shake' : ''}`}>
                        {/* Header */}
                        <div className="hidden lg:block text-center space-y-2">
                            <h3 className="text-2xl font-bold text-gray-900">Welcome Back</h3>
                            <p className="text-gray-500 text-sm">Sign in to your account to continue</p>
                            {loginAttempts > 0 && !isLocked && !localError && !error && (
                                <p className="text-xs text-amber-600 mt-1 flex items-center justify-center gap-1">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    {5 - loginAttempts} attempts remaining
                                </p>
                            )}
                        </div>

                        {/* Session Expired Alert */}
                        {sessionExpired && (
                            <div className="bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded-2xl flex items-start gap-3 animate-fadeIn shadow-sm">
                                <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <div className="flex-1">
                                    <p className="text-sm font-medium">Session Expired</p>
                                    <p className="text-xs mt-0.5 opacity-80">
                                        Please log in again to continue.
                                    </p>
                                </div>
                                <button
                                    onClick={() => setSessionExpired(false)}
                                    className="text-amber-400 hover:text-amber-600 flex-shrink-0 transition-colors"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        )}

                        {/* Login Error Alert */}
                        {(localError || error) && !isLocked && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-2xl flex items-start gap-3 animate-fadeIn shadow-sm">
                                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
                                    <svg className="w-3 h-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </div>
                                <span className="text-sm flex-1">{getErrorMessage()}</span>
                                <button
                                    type="button"
                                    onClick={clearErrorMessage}
                                    className="text-red-400 hover:text-red-600 transition-colors flex-shrink-0"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        )}

                        {/* Locked Alert */}
                        {isLocked && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-2xl flex items-start gap-3 animate-fadeIn shadow-sm">
                                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
                                    <svg className="w-3 h-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm font-medium">Account Temporarily Locked</p>
                                    <p className="text-xs mt-0.5">
                                        Too many failed attempts. Please wait <strong className="text-red-800">{lockTimer}</strong> seconds before trying again.
                                    </p>
                                </div>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                            {/* Email Field */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    Email Address
                                </label>
                                <div className="relative">
                                    <div className={`absolute left-0 top-0 bottom-0 w-12 flex items-center justify-center transition-colors duration-200 ${emailFocused || email ? 'text-[#3a663f]' : 'text-gray-400'
                                        }`}>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={handleEmailChange}
                                        onFocus={() => setEmailFocused(true)}
                                        onBlur={() => setEmailFocused(false)}
                                        placeholder="you@example.com"
                                        className={`w-full pl-12 pr-4 py-3.5 border-2 rounded-2xl focus:ring-4 focus:ring-[#3a663f]/10 focus:border-[#3a663f] outline-none text-gray-700 placeholder-gray-400 transition-all duration-200 ${(localError || error) && !isLocked ? 'border-red-300 bg-red-50' : 'border-gray-200'
                                            }`}
                                        required
                                        autoComplete="email"
                                        disabled={isLocked}
                                    />
                                </div>
                            </div>

                            {/* Password Field */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    Password
                                </label>
                                <div className="relative">
                                    <div className={`absolute left-0 top-0 bottom-0 w-12 flex items-center justify-center transition-colors duration-200 ${passwordFocused || password ? 'text-[#3a663f]' : 'text-gray-400'
                                        }`}>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                    </div>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={handlePasswordChange}
                                        onFocus={() => setPasswordFocused(true)}
                                        onBlur={() => setPasswordFocused(false)}
                                        placeholder="••••••••"
                                        className={`w-full pl-12 pr-12 py-3.5 border-2 rounded-2xl focus:ring-4 focus:ring-[#3a663f]/10 focus:border-[#3a663f] outline-none text-gray-700 placeholder-gray-400 transition-all duration-200 ${(localError || error) && !isLocked ? 'border-red-300 bg-red-50' : 'border-gray-200'
                                            }`}
                                        required
                                        autoComplete="current-password"
                                        disabled={isLocked}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-lg hover:bg-gray-100"
                                        disabled={isLocked}
                                    >
                                        {showPassword ? (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M21 12a9.97 9.97 0 01-1.543 2.971M3 3l18 18" />
                                            </svg>
                                        ) : (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                                {(localError || error) && !isLocked && (
                                    <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1 animate-fadeIn">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        {getErrorMessage()}
                                    </p>
                                )}
                            </div>

                            {/* Remember & Forgot */}
                            <div className="flex items-center justify-between text-sm">
                                <label className="flex items-center gap-2.5 text-gray-600 cursor-pointer group">
                                    <div className="relative">
                                        <input
                                            type="checkbox"
                                            checked={rememberMe}
                                            onChange={(e) => setRememberMe(e.target.checked)}
                                            className="w-4 h-4 rounded border-gray-300 text-[#3a663f] focus:ring-[#3a663f] focus:ring-2 transition-all cursor-pointer"
                                            disabled={isLocked}
                                        />
                                    </div>
                                    <span className="group-hover:text-gray-800 transition-colors">Remember me</span>
                                </label>
                                <button
                                    type="button"
                                    onClick={handleForgotPassword}
                                    className="text-[#3a663f] hover:text-[#2d5232] font-medium transition-colors text-sm bg-transparent border-none cursor-pointer"
                                >
                                    Forgot password?
                                </button>
                            </div>

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={isLoading || isLocked}
                                className="relative w-full group overflow-hidden bg-gradient-to-r from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54] text-white font-semibold py-4 rounded-2xl shadow-lg shadow-[#3a663f]/20 hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]"
                            >
                                <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></span>
                                <span className="relative flex items-center justify-center gap-3 text-lg">
                                    {isLoading ? (
                                        <>
                                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            Signing in...
                                        </>
                                    ) : isLocked ? (
                                        <>
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                            </svg>
                                            Locked ({lockTimer}s)
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                                            </svg>
                                            Sign In
                                            <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </>
                                    )}
                                </span>
                            </button>
                        </form>

                        {/* Divider */}
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-200"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-4 bg-white text-gray-400">New to Mayfair insurance Marine Portal?</span>
                            </div>
                        </div>

                        {/* Register Link */}
                        <Link
                            to="/register"
                            className="w-full flex items-center justify-center gap-2 px-4 py-3.5 border-2 border-gray-200 hover:border-[#3a663f]/30 text-gray-700 hover:text-[#3a663f] font-medium rounded-2xl transition-all duration-200 group hover:shadow-md"
                        >
                            <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                            </svg>
                            Create an Account
                        </Link>
                    </div>

                    {/* Footer */}

                    <div className="text-center text-xs text-gray-400 space-y-1">
                        <p>© 2026 Mayfair Insurance Company Limited. All rights reserved.</p>

                    </div>

                </div>
            </div>

            {/* CSS Animations */}
            <style>{`
                @keyframes float {
                    0%, 100% { transform: translateY(0px) scale(1); }
                    50% { transform: translateY(-20px) scale(1.02); }
                }
                @keyframes float-delayed {
                    0%, 100% { transform: translateY(0px) scale(1); }
                    50% { transform: translateY(20px) scale(1.02); }
                }
                @keyframes pulse-slow {
                    0%, 100% { opacity: 0.3; }
                    50% { opacity: 0.6; }
                }
                @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    10% { transform: translateX(-10px); }
                    20% { transform: translateX(10px); }
                    30% { transform: translateX(-10px); }
                    40% { transform: translateX(10px); }
                    50% { transform: translateX(-5px); }
                    60% { transform: translateX(5px); }
                    70% { transform: translateX(-3px); }
                    80% { transform: translateX(3px); }
                    90% { transform: translateX(-1px); }
                }
                .animate-float {
                    animation: float 8s ease-in-out infinite;
                }
                .animate-float-delayed {
                    animation: float-delayed 10s ease-in-out infinite;
                }
                .animate-pulse-slow {
                    animation: pulse-slow 4s ease-in-out infinite;
                }
                .animate-shake {
                    animation: shake 0.5s ease-in-out;
                }
            `}</style>
        </div>
    );
}